<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <section
    class="HotelExperiences"
    :aria-label="
      $t('accessibility.description.hotelExperiencesValueproposition')
    ">
    <div class="HotelExperiences-container">
      <div class="Layout">
        <div class="Layout-section">
          <grid-item sm="4" offset-md="1" md="10">
            <!-- Header -->
            <div class="HotelExperiences-header">
              <ai-typo
                class="HotelExperiences-header-subtitle"
                as="p"
                variant="overline-01">
                {{ $t('hotel.experiences.subtitle') }}
              </ai-typo>

              <ai-typo
                class="HotelExperiences-header-title"
                as="h2"
                variant="heading-02"
                aria-level="2"
                v-html="$t('hotel.experiences.title')" />
            </div>
          </grid-item>
        </div>

        <div
          class="HotelExperiences-imageContainer HotelExperiences-imageContainer--mobile"
          role="complementary">
          <ai-image
            v-for="(experience, index) of experiences ?? []"
            :key="experience.id"
            class="HotelExperiences-image"
            :class="{
              'HotelExperiences-image--visible': index === currentStep - 1,
            }"
            :src="experience?.picture?.src"
            :src-set="experience?.picture?.srcSet as string"
            :alt="experience?.picture?.alt as string" />
        </div>
      </div>

      <div
        class="Layout HotelExperience-carouselMobile"
        :aria-label="$t('accessibility.description.hotelExperiencesCarousel')">
        <div class="Layout-section">
          <grid-item sm="4">
            <!-- Carousel -->
            <div class="HotelExperiences-carousel">
              <ai-carousel
                v-model="currentStep"
                mobile-button
                mobile-button-variant="outline"
                button-position="center"
                :slide-width="0.65"
                :items="items"
                position="full"
                hide-gallery />
            </div>
          </grid-item>
        </div>
      </div>

      <div
        class="Layout HotelExperience-carouselDesktop"
        :aria-label="$t('accessibility.description.hotelExperiencesCarousel')"
        aria-roledescription="carousel">
        <div class="Layout-section">
          <grid-item md="1" offset-md="1" class="HotelExperience-actions">
            <div>
              <ai-carousel-button
                class="HotelExperience-actionButtons"
                variant="outline"
                vertical
                access-id="hotelExperiencesCarousel"
                :accessibility-label="
                  $t('hotel.experiences.accessibilityLabel')
                "
                @top-click="previousStep"
                @bottom-click="nextStep" />
            </div>
          </grid-item>
          <grid-item md="5">
            <div class="HotelExperiences-imageContainer">
              <ai-image
                v-for="(experience, index) of experiences ?? []"
                :key="experience.id"
                class="HotelExperiences-image"
                :class="{
                  'HotelExperiences-image--visible': index === currentStep - 1,
                }"
                :src="experience?.picture?.src"
                :src-set="experience?.picture?.srcSet as string"
                :alt="experience?.picture?.alt as string"
                loading="lazy" />
            </div>
          </grid-item>
          <grid-item md="5" offset-md="-1">
            <ai-carousel-vertical
              v-model="currentStep"
              :aria-label="
                $t('accessibility.description.hotelExperiencesCarousel')
              "
              access-id="hotelExperiencesCarousel"
              :items="items" />
          </grid-item>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import HotelExperienceCarouselItem from './HotelExperienceCarouselItem.vue';

import type { HotelExperience } from '~~/domains/graphql';
import type { AiCarouselItem } from '~~/domains/ux/molecules/AiCarousel/types';

interface Props {
  experiences: HotelExperience[];
}

const props = defineProps<Props>();

const currentStep = ref<number>(1);

const items = computed<AiCarouselItem[]>(() =>
  props.experiences.map<AiCarouselItem>(item => ({
    component: HotelExperienceCarouselItem,
    key: `hotel-experience-${item.id}`,
    props: {
      subtitle: item.subtitle,
      title: item.title,
      link: item.link,
    },
  })),
);

const previousStep = () => {
  if (currentStep.value <= 1) return;
  currentStep.value--;
};

const nextStep = () => {
  if (currentStep.value >= props.experiences.length) return;
  currentStep.value++;
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/functions' as func;
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/grid';
@use '@/assets/styles/utilities/mixins';

.HotelExperiences {
  background: constants.$linear-gradient-stratos;
  padding-top: constants.$padding-07;
  padding-bottom: constants.$padding-03;

  @media (mq.$from-medium) {
    padding-top: constants.$padding-09;
  }
}

.HotelExperiences-container {
  padding-bottom: constants.$padding-05;
  overflow: hidden;

  @media (mq.$from-medium) {
    padding-bottom: constants.$padding-07;
  }
}

.HotelExperiences-header {
  margin-bottom: constants.$margin-04;
  text-align: center;

  @media (mq.$from-medium) {
    text-align: left;
  }
}

.HotelExperiences-header-subtitle {
  text-transform: uppercase;
  color: colors.$gold-300;
  margin-bottom: constants.$margin-01;
  padding: 0 constants.$padding-01;
  letter-spacing: 0.1em;

  @media (mq.$from-medium) {
    padding: 0;
    margin-bottom: constants.$margin-02;
  }
}

.HotelExperiences-header-title {
  color: colors.$white;
  @media (mq.$from-medium) {
    @include mixins.rem-fallback(margin-bottom, 72);
  }
}

.HotelExperiences-imageContainer {
  position: relative;
  margin-bottom: constants.$margin-04;
  @include mixins.rem-fallback(height, 212);

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(height, 400);
  }

  &--mobile {
    @media (mq.$from-medium) {
      display: none;
    }
  }

  &::after {
    position: absolute;
    content: '';
    height: func.calcRem(64);
    width: 1px;
    bottom: calc(-64px / 2);
    left: 50%;
    background: linear-gradient(colors.$gold-400, #b4a885, colors.$gold-700);

    @media (mq.$from-medium) {
      display: none;
    }
  }
}

.HotelExperiences-image {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: inline-block;
  opacity: 0;
  transform: translateX(-50%);
  transition: all 500ms;
  z-index: 0;

  &--visible {
    opacity: 1;
    transform: translateX(0);
  }

  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    inset: 0;
    background: linear-gradient(
      270deg,
      rgba(22, 21, 47, 0.5) 0%,
      rgba(22, 21, 47, 0.175) 37.5%,
      rgba(0, 0, 0, 0) 73.21%
    );
  }
}

.HotelExperience-carouselMobile {
  @media (mq.$from-medium) {
    display: none;
  }
}

.HotelExperience-carouselDesktop {
  display: none;

  @media (mq.$from-medium) {
    display: inherit;
  }
}

.HotelExperience-actions {
  display: none;

  @media (mq.$from-medium) {
    z-index: 5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    @include mixins.rem-fallback(height, 400);
  }
}

.HotelExperiences-carousel {
  width: 100%;
  padding: constants.$padding-00;
}
</style>
